import { Loading } from "@quark/design";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getExamLogDetail,
  getUserConfig,
  updateUserConfig,
} from "../../../api";
import Navigator from "../../../component/navigator";
import ProgressWithStepper from "../../../component/progress-with-stepper";
import { UserConfig } from "../../../types";
import { ExamDetail, ExamMode } from "../../../types/exam";
import ExamPracticeFooter from "./footer";
import styles from "./index.module.scss";
import Quiz from "./quiz";
import RightPanel from "./right-panel";
import { UserConfigContext } from "./store";
import useDocumentTitle from "../../../hooks/use-document-title";

const ExamPractice = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { examId, logId } = params;
  const [query] = useSearchParams();
  const defaultIndex = Number(query.get("active")) || 0;
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const [exam, setExam] = useState<ExamDetail>();
  const defaultMode = (query.get("mode") as ExamMode) || "practice";
  const [mode, setMode] = useState<ExamMode>(defaultMode);
  useDocumentTitle(`二级刷题/${examId}`);
  const [userConfig, setUserConfig] = useState<UserConfig>({
    exam: {
      auto_next: false,
    },
  });
  const [loading, setLoading] = useState(false);

  const queryParams = useMemo(() => {
    return {
      mode,
      active: String(activeIndex),
    };
  }, [mode, activeIndex]);

  const progress = useMemo(() => {
    const { log } = exam || {};
    if (log) {
      return log.right_num + log.wrong_num;
    }
    return 0;
  }, [exam]);

  const getExam = useCallback(() => {
    return getExamLogDetail(Number(examId), Number(logId)).then((data) => {
      setExam(data);
      return data;
    });
  }, [examId, logId]);

  const updateConfig = useCallback((config: UserConfig) => {
    return updateUserConfig(config).then((res) => {
      setUserConfig(res);
      return res;
    });
  }, []);

  // 答案正确时
  const handleAnswerRight = () => {
    // todo 播放音频
    if (userConfig.exam.auto_next) {
      if (activeIndex < (exam?.quiz_num || 0) - 1) {
        const nextIndex = activeIndex + 1;
        setTimeout(() => {
          setActiveIndex(nextIndex);
        }, 800);
      }
    }
  };

  // 加载用户设置
  useEffect(() => {
    getUserConfig().then((config) => {
      setUserConfig(config);
    });
  }, []);

  // 更新 search query
  useEffect(() => {
    const query = new URLSearchParams(queryParams);
    navigate(`?${query}`, { replace: true });
  }, [queryParams, navigate]);

  useEffect(() => {
    setLoading(true);
    getExam()
      .then((data) => {
        const isContinue = query.get("continue");
        if (!activeIndex && isContinue) {
          // 跳转到第一个没有提交答案的quiz
          const firstUndoIndex =
            data.quizs.findIndex((quiz) => !quiz.user_answers) || 0;
          setActiveIndex(firstUndoIndex);
        }
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, logId, getExam]);

  return (
    <UserConfigContext.Provider value={{ config: userConfig, updateConfig }}>
      <div className={styles.container}>
        <Navigator activeKey="exam" />

        <div className={styles.content}>
          {loading && <Loading />}
          {!loading && !!exam && !!userConfig && (
            <>
              <div className={styles.left}>
                <div className={styles.progress}>
                  <ProgressWithStepper
                    className={styles.bar}
                    lastStep={progress}
                    currStep={activeIndex}
                    total={exam.quiz_num}
                  />
                </div>

                <div>
                  {exam.quizs.map((quiz, index) => (
                    <Quiz
                      visible={activeIndex === index}
                      key={quiz.question_id}
                      dataSource={quiz}
                      examSubmitted={exam.log?.status === "done"}
                      index={index}
                      mode={mode}
                      logId={exam.log?.id || 0}
                      onChange={() => getExam()}
                      onRight={handleAnswerRight}
                    />
                  ))}
                </div>
              </div>
              <div className={styles.right}>
                <RightPanel
                  mode={mode}
                  exam={exam}
                  currentIndex={activeIndex}
                  onModeChange={(m) => setMode(m)}
                  onIndexChange={(index) => setActiveIndex(index)}
                  onSubmit={() => window.location.reload()}
                />
              </div>
            </>
          )}
        </div>

        {!!exam && (
          <ExamPracticeFooter
            currentIndex={activeIndex}
            exam={exam}
            onPrev={() => setActiveIndex(activeIndex - 1)}
            onNext={() => setActiveIndex(activeIndex + 1)}
          />
        )}
      </div>
    </UserConfigContext.Provider>
  );
};

export default ExamPractice;
